import * as MutationHelpers from 'shared/helpers/vuex/mutationHelpers';
import * as types from '../mutation-types';
import AgentAPI from '../../api/agents';
import TeamsAPI from '../../api/teams';
import InboxMembersAPI from '../../api/inboxMembers';
import BrandsAPI from '../../api/brands';
import * as Sentry from '@sentry/browser';

export const state = {
  records: [],
  teamMembers: [],
  inboxMembers: [],
  brandMembers: [],
  filteredAgents: null,
  uiFlags: {
    isFetching: false,
    isCreating: false,
    isUpdating: false,
    isDeleting: false,
  },
};

export const getters = {
  getAgents($state) {
    return $state.records;
  },
  getInboxMembers($state) {
    return $state.records;
  },
  getTeamMembers($state) {
    return $state.records;
  },
  getBrandMembers($state) {
    return $state.records;
  },
  getVerifiedAgents($state) {
    return $state.records.filter(record => record.confirmed);
  },
  getUIFlags($state) {
    return $state.uiFlags;
  },
  getAgentStatus($state) {
    let status = {
      online: $state.records.filter(
        agent => agent.availability_status === 'online'
      ).length,
      busy: $state.records.filter(agent => agent.availability_status === 'busy')
        .length,
      offline: $state.records.filter(
        agent => agent.availability_status === 'offline'
      ).length,
    };
    return status;
  },
  getMyAgents($state) {
    const brandMemberIds = $state.brandMembers.map(member => member.id);
    return $state.records.filter(agent => brandMemberIds.includes(agent.id));
  },
  getMyTeamAndInboxAgents($state) {
    const teamMemberIds = $state.teamMembers.map(member => member.id);
    const inboxMemberIds = $state.inboxMembers.map(member => member.id);
    
    // Combine team and inbox member IDs and remove duplicates
    const combinedMemberIds = [...new Set([...teamMemberIds, ...inboxMemberIds])];
    
    return $state.records.filter(agent => combinedMemberIds.includes(agent.id));
  },
};

export const actions = {
  get: async ({ commit }) => {
    commit(types.default.SET_AGENT_FETCHING_STATUS, true);
    try {
      const response = await AgentAPI.get();
      commit(types.default.SET_AGENT_FETCHING_STATUS, false);
      commit(types.default.SET_AGENTS, response.data);
    } catch (error) {
      commit(types.default.SET_AGENT_FETCHING_STATUS, false);
    }
  },
  getBrandMembers: async ({ commit }, { brandIds }) => {
    if (!brandIds || !brandIds.length) return;

    const responses = await Promise.all(
      brandIds.map(brandId =>
        BrandsAPI.getAgents({ brandId }).catch(error => {
          Sentry.captureException(error)
          throw new Error(error);
          return { data: [] };
        })
      )
    );

    const members = responses.reduce((acc, { data }) => [...acc, ...data], []);
    commit(types.default.SET_BRAND_MEMBERS, members);
  },
  getInboxMembers: async ({ commit }, { inboxIds }) => {
    try {
      if (!inboxIds || !inboxIds.length) return;

      const responses = await Promise.all(
        inboxIds.map(inboxId => 
          InboxMembersAPI.get(inboxId).catch(error => {
            console.error(`Error fetching inbox members for inbox ${inboxId}:`, error);
            return { data: [] };
          })
        )
      );

      const members = responses.flatMap(response => response.data);
      commit(types.default.SET_INBOX_MEMBERS, members);
    } catch (error) {
     Sentry.captureException(error);
      throw new Error(error);
    }
  },
  getTeamMembers: async ({ commit }, { teamIds }) => {
    try {
      // Create an array of promises for each teamId's team members
      const membersPromises = teamIds.map(teamId =>
        TeamsAPI.getAgents({ teamId }).then(response => response.data)
      );

      // Wait for all promises to resolve
      const membersArrays = await Promise.all(membersPromises);

      // Flatten the array of arrays into a single array of members
      const members = [].concat(...membersArrays);
      
      // Commit the retrieved team members to the store
      commit(types.default.SET_TEAM_MEMBERS, members);
    } catch (error) {
      throw new Error(error);
    }
  },
  create: async ({ commit }, agentInfo) => {
    commit(types.default.SET_AGENT_CREATING_STATUS, true);
    try {
      const response = await AgentAPI.create(agentInfo);
      const agent = {
        ...response.data,
        availability_status: 'offline',
        active: true
      };
      commit(types.default.ADD_AGENT, agent);
      commit(types.default.SET_AGENT_CREATING_STATUS, false);
    } catch (error) {
      commit(types.default.SET_AGENT_CREATING_STATUS, false);
      throw error;
    }
  },
  update: async ({ commit }, { id, ...agentParams }) => {
    commit(types.default.SET_AGENT_UPDATING_STATUS, true);
    try {
      const response = await AgentAPI.update(id, agentParams);
      commit(types.default.EDIT_AGENT, response.data);
      commit(types.default.SET_AGENT_UPDATING_STATUS, false);
    } catch (error) {
      commit(types.default.SET_AGENT_UPDATING_STATUS, false);
      throw new Error(error);
    }
  },
  updateSingleAgentPresence: ({ commit }, { id, availabilityStatus }) => {
    commit(types.default.UPDATE_SINGLE_AGENT_PRESENCE, {
      id,
      availabilityStatus,
    });
  },
  updatePresence: async ({ commit }, data) => {
    commit(types.default.UPDATE_AGENTS_PRESENCE, data);
  },
  delete: async ({ commit }, agentId) => {
    commit(types.default.SET_AGENT_DELETING_STATUS, true);
    try {
      await AgentAPI.delete(agentId);
      commit(types.default.DELETE_AGENT, agentId);
      commit(types.default.SET_AGENT_DELETING_STATUS, false);
    } catch (error) {
      commit(types.default.SET_AGENT_DELETING_STATUS, false);
      throw new Error(error);
    }
  },
  bulkDelete: async ({ commit }, agentIds) => {
    commit(types.default.SET_AGENT_DELETING_STATUS, true);
    try {
      await Promise.all(agentIds.map(id => AgentAPI.delete(id)));
      commit(types.default.BULK_DELETE_AGENTS, agentIds);
      commit(types.default.SET_AGENT_DELETING_STATUS, false);
    } catch (error) {
      commit(types.default.SET_AGENT_DELETING_STATUS, false);
      throw error;
    }
  },
  bulkMove: async ({ commit }, { agentIds, teamIds, inboxIds, brandIds }) => {
    try {
      const response = await AgentAPI.bulkMove({
        agent_ids: agentIds,
        team_ids: teamIds,
        inbox_ids: inboxIds,
        brand_ids: brandIds,
      });
      return response.data;
    } catch (error) {
      throw new Error(error);
    }
  },  
  toggleActivation: async ({ commit }, { agentIds, active }) => {
    commit(types.default.SET_AGENT_UPDATING_STATUS, true);
    try {
      await AgentAPI.toggleActivation(agentIds, active);
      commit(types.default.TOGGLE_AGENT_ACTIVATION, { agentIds, active });
      commit(types.default.SET_AGENT_UPDATING_STATUS, false);
    } catch (error) {
      commit(types.default.SET_AGENT_UPDATING_STATUS, false);
      throw error;
    }
  },
  applyFilters({ commit }, { agents, filters, inboxMembers, teamMembers, brandMembers }) {
    // Extract and deduplicate members from all inbox payloads
    const extractedInboxMembers = inboxMembers.reduce((acc, inboxMemberGroup) => {
      const members = inboxMemberGroup.payload || [];
    
      members.forEach(member => {
        if (!acc.some(existingMember => existingMember.id === member.id)) {
          acc.push(member);
        }
      });
        
      return acc;
    }, []);

    const filteredAgents = agents.filter(agent => {
      return filters.every(filter => {
        switch (filter.attribute_key) {
          case 'role':
            const roleValues = filter.values.map(v => v.id.toLowerCase());
            const agentRole = agent.role.toLowerCase();
            return filter.filter_operator === 'equal_to' 
              ? roleValues.includes(agentRole)
              : !roleValues.includes(agentRole);
          case 'status':
            const statusValues = filter.values.map(v => v.id);
            const agentStatus = agent.active ? 'active' : 'inactive';
            return filter.filter_operator === 'equal_to'
              ? statusValues.includes(agentStatus)
              : !statusValues.includes(agentStatus);         

          case 'teams':
            const isInTeams = teamMembers.some(member => 
              member.id === agent.id
            );
  
            return filter.filter_operator === 'equal_to'
              ? isInTeams
              : !isInTeams;
          
          case 'brands':
            const isInBrands = brandMembers.some(member => 
              member.id === agent.id
            );
  
            return filter.filter_operator === 'equal_to'
              ? isInBrands
              : !isInBrands;
              
          case 'inboxes':
            // Since inboxMembers are already filtered, we just need to check if the agent is in the list
            const isInInboxes = extractedInboxMembers.some(member => member.id === agent.id);

            // Handle different filter operators
            return filter.filter_operator === 'equal_to'
              ? isInInboxes
              : !isInInboxes;
        
          default:
            return true;
        }
      });
    });
    commit('SET_FILTERED_AGENTS', filteredAgents);
  }
};

export const mutations = {
  [types.default.SET_AGENT_FETCHING_STATUS]($state, status) {
    $state.uiFlags.isFetching = status;
  },
  [types.default.SET_AGENT_CREATING_STATUS]($state, status) {
    $state.uiFlags.isCreating = status;
  },
  [types.default.SET_AGENT_UPDATING_STATUS]($state, status) {
    $state.uiFlags.isUpdating = status;
  },
  [types.default.SET_AGENT_DELETING_STATUS]($state, status) {
    $state.uiFlags.isDeleting = status;
  },
  [types.default.SET_BRAND_MEMBERS]($state, brandMembers) {
    $state.brandMembers = brandMembers;
  },
  [types.default.SET_TEAM_MEMBERS]($state, teamMembers) {
    $state.teamMembers = teamMembers;
  },
  [types.default.SET_INBOX_MEMBERS]($state, inboxMembers) {
    $state.inboxMembers = inboxMembers;
  },
  [types.default.TOGGLE_AGENT_ACTIVATION]($state, { agentIds, active }) {
    $state.records = $state.records.map(agent => {
      if (agentIds.includes(agent.id)) {
        return { 
          ...agent, 
          active: active 
        };
      }
      return agent;
    });

    // Update filtered agents if they exist
    if ($state.filteredAgents) {
      $state.filteredAgents = $state.filteredAgents.map(agent => {
        if (agentIds.includes(agent.id)) {
          return {
            ...agent,
            active: active
          };
        }
        return agent;
      });
    }
  },
  [types.default.BULK_DELETE_AGENTS]($state, agentIds) {
    $state.records = $state.records.filter(agent => !agentIds.includes(agent.id));
  },
  [types.default.SET_FILTERED_AGENTS](state, agents) {
    state.filteredAgents = agents;
  },
  [types.default.SET_AGENTS]: MutationHelpers.set,
  [types.default.ADD_AGENT]: MutationHelpers.create,
  [types.default.EDIT_AGENT]: MutationHelpers.update,
  [types.default.DELETE_AGENT]: MutationHelpers.destroy,
  [types.default.UPDATE_AGENTS_PRESENCE]: MutationHelpers.updatePresence,
  [types.default.UPDATE_SINGLE_AGENT_PRESENCE]: (
    $state,
    { id, availabilityStatus }
  ) =>
    MutationHelpers.updateSingleRecordPresence($state.records, {
      id,
      availabilityStatus,
    }),
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};